<template>
  <div class="shared-deadlines">
    <div class="info-form__body">
      <v-form ref="infoForm" class="info-form__main-form">
        <v-row class="row">
          <v-col cols="10" class="d-flex align-center">
            <h2>
              <span class="red--text">
                <v-icon color="black" class="mb-1">mdi-calendar</v-icon>
              </span>
              Shared Deadline (for {{ sharedDeadlineAssignmentsCount }} out of {{ totalAssignmentsCount }})
            </h2>
          </v-col>
          <v-col cols="2" class="d-flex justify-end">
            <v-menu 
              rounded="lg"
              transition="slide-y-transition"
              offset-y>
              <template v-slot:activator="{ on, attrs }">
                <button
                  v-bind="attrs"
                  v-on="on"
                  class="info-form__dropdown-button"
                  @click.prevent="showSharedDropdown = !showSharedDropdown"
                >
                  <v-icon>mdi-dots-horizontal</v-icon>
                </button>
              </template>
              <v-list>
                <v-list-item 
                  @click.prevent="showDeadlineConfirmation = true"
                  class="wtMenuList__item">
                  <v-list-item-title>Apply deadline for all assignments</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-col>
        </v-row>
        <v-row class="row mt-3">
          <v-col cols="4">
            <v-menu
              v-model="requestDateTimeMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="formattedRequestDateTime"
                  label="Request Date / Time"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  hide-details
                  filled
                ></v-text-field>
              </template>
              <div class="date-time-picker-menu-content" @mousedown.stop @click.stop>
                <CustomDateTimePicker 
                  :preparedDate="formValues.requestDate" 
                  :preparedTime="formValues.requestTime"
                  @onDateSelected="onRequestDateSelected"
                  @onTimeSelected="onRequestTimeSelected($event)"/>
              </div>
            </v-menu>
          </v-col>
          <v-col cols="4">
            <v-menu
              ref="deliveryDateTimeMenu"
              v-model="deliveryDateTimeMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="formattedDeliveredDateTime"
                  label="Delivered Date / Time"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  hide-details
                  filled
                ></v-text-field>
              </template>
              <div class="date-time-picker-menu-content" @mousedown.stop @click.stop>
                <CustomDateTimePicker 
                  :preparedDate="formValues.defaultDeliveryDate" 
                  :preparedTime="formValues.defaultDeliveryDue"
                  @onDateSelected="onDefaultDeliveryDateSelected"
                  @onTimeSelected="onDefaultDeliveryTimeSelected($event)"/>
              </div>
            </v-menu>
          </v-col>
          <v-col cols="4">
            <v-autocomplete
              filled
              label="Time Zone"
              hide-details
              :error="conversionErrors"
              v-model="timeZone"
              :items="utcTimeZones"
            ></v-autocomplete>
          </v-col>
        </v-row>
        <h4 class="mt-7">Converted to ET</h4>
        <v-row class="row mt-3">
          <v-col cols="4">
            <v-menu
              v-model="primaryDateTimeMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="formattedPrimaryDateTime"
                  label="Primary Due Date / Time (ET)"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  hide-details
                  filled
                ></v-text-field>
              </template>
              <div class="date-time-picker-menu-content" @mousedown.stop @click.stop>
                <CustomDateTimePicker 
                  :preparedDate="formValues.writingDueDate" 
                  :preparedTime="formValues.writingDue"
                  @onDateSelected="onPrimaryDateSelected"
                  @onTimeSelected="onPrimaryTimeSelected($event)"/>
              </div>
            </v-menu>
          </v-col>
          <v-col cols="4">
            <v-menu
              v-model="secondaryDateTimeMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="formattedSecondaryDateTime"
                  label="Secondary Due Date / Time (ET)"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  hide-details
                  filled
                ></v-text-field>
              </template>
              <div class="date-time-picker-menu-content" @mousedown.stop @click.stop>
                <CustomDateTimePicker 
                  :preparedDate="formValues.editingDueDate" 
                  :preparedTime="formValues.editingDue"
                  @onDateSelected="onSecondaryDateSelected"
                  @onTimeSelected="onSecondaryTimeSelected($event)"/>
              </div>
            </v-menu>
          </v-col>
          <v-col cols="4">
            <v-menu
              v-model="qcDateTimeMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="formattedQcDateTime"
                  label="QC Due Date / Time (ET)"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  hide-details
                  filled
                ></v-text-field>
              </template>
              <div class="date-time-picker-menu-content" @mousedown.stop @click.stop>
                <CustomDateTimePicker 
                  :preparedDate="formValues.qcDueDate || formValues.reviewingDueDate" 
                  :preparedTime="formValues.qcDue || formValues.reviewingDue"
                  @onDateSelected="onQCDateSelected"
                  @onTimeSelected="onQCTimeSelected($event)"/>
              </div>
            </v-menu>
          </v-col>
        </v-row>
        <v-row class="row mt-3">
          <v-col cols="12">
            <v-menu
              v-model="deliveryEtDateTimeMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="formattedDeliveryEtDateTime"
                  label="Delivery Date / Time (ET)"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  hide-details
                  filled
                ></v-text-field>
              </template>
              <div class="date-time-picker-menu-content" @mousedown.stop @click.stop>
                <CustomDateTimePicker 
                  :preparedDate="formValues.deliveryDate" 
                  :preparedTime="formValues.deliveryDue"
                  @onDateSelected="onDeliveryDateSelected"
                  @onTimeSelected="onDeliveryTimeSelected($event)"/>
              </div>
            </v-menu>
          </v-col>
        </v-row>
        <v-row class="row mt-3">
          <v-col cols="12">
            <v-checkbox
              class="mt-0"
              v-model="formValues.override"
              label="Override Due Dates"
              hide-details
              :disabled="!formValues.deliveryDate && !formValues.deliveryDue"
              @change="setFormValue({ key: 'override', value: $event })"
            ></v-checkbox>
          </v-col>
        </v-row>
      </v-form>
    </div>
    <v-dialog
      v-model="showDeadlineConfirmation"
      max-width="500"
      persistent
      @click:outside="showDeadlineConfirmation = false"
      @keydown.esc="showDeadlineConfirmation = false"
      ref="deadlineDialog"
    >
      <div class="deadline-dialog">
        <h4 class="deadline-dialog__title">Apply shared deadline for all assignments?</h4>
        <p class="deadline-dialog__text">Are you sure you want to apply deadline for all assignments?</p>
        <div class="deadline-dialog__buttons">
          <v-btn
              text
              @click.prevent="showDeadlineConfirmation = false"
            >
              CANCEL
            </v-btn>
            <v-btn
              color="#1976D2"
              class="deadline-dialog__buttons--confirm ml-2"
            >
              APPLY TO ALL
            </v-btn>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { timeUnits } from "@/utils/mixins";
import CustomDateTimePicker from "@/components/ui/CustomDateTimePicker.vue";
export default {
  name: "SharedDeadlines",
  components: {
    CustomDateTimePicker,
  },
  props: {
    assignmentsData: {
      required: true,
      type: Array
    }
  },
  mixins: [timeUnits],
  data() {
    return {
      requestDateTimeMenu: false,
      deliveryDateTimeMenu: false,
      primaryDateTimeMenu: false,
      secondaryDateTimeMenu: false,
      qcDateTimeMenu: false,
      deliveryEtDateTimeMenu: false,
      conversionErrors: false,
      showSharedDropdown: false,
      showDeadlineConfirmation: false,
      timeZone: "",
    }
  },
  watch: {
    /* 'formValues.defaultDeliveryDate': {
      handler() {
        console.log("this.formValues", this.formValues);
        if (this.formValues.defaultDeliveryDate && this.formValues.deliveryDue && this.timeZone) {
          this.convertDeliveryDue();
        }
      },
      deep: true,
    }, */
    'formValues.defaultDeliveryDue': {
      handler() {
        if (this.formValues.defaultDeliveryDate && this.formValues.defaultDeliveryDue && this.timeZone) {
          this.convertDeliveryDue();
        }
      },
    },
    'formValues.defaultDeliveryDate': {
      handler(newDate) {
        this.updateUrgentFlag(newDate || this.formValues.deliveryDate);
      },
    },
    'formValues.deliveryDate': {
      handler(newDate) {
        this.updateUrgentFlag(this.formValues.defaultDeliveryDate || newDate);
      },
    },
    'formValues.requestDate': {
      handler() {
        this.updateUrgentFlag(this.formValues.defaultDeliveryDate || this.formValues.deliveryDate);
      },
    },
    tableRows: {
      handler() {
        this.updateUrgentFlag(this.formValues.defaultDeliveryDate || this.formValues.deliveryDate);
      },
      deep: false,
    },
    timeZone() {
      console.log("this.timeZone", this.timeZone);
      if (this.formValues.defaultDeliveryDate && this.formValues.defaultDeliveryDue && this.timeZone) {
        this.convertDeliveryDue();
      }
    },
  },
  computed: {
    ...mapGetters("spreadsheetUpload", [
      "formValues",
      "staffAssignments",
      "tableRows"
    ]),
    totalAssignmentsCount() {
      return Array.isArray(this.assignmentsData) ? this.assignmentsData.length : 0;
    },
    sharedDeadlineAssignmentsCount() {
      if (!Array.isArray(this.assignmentsData)) return 0;
      return this.assignmentsData.filter((assignment) => !assignment?.individualDeadline).length;
    },
    formattedRequestDateTime() {
      if (this.formValues.requestDate && this.formValues.requestTime) {
        const datePart = new Date(this.formValues.requestDate);
        const day = datePart.getDate();
        const month = datePart.toLocaleString('en', { month: 'short' });
        const year = datePart.getFullYear();
        const time = this.formValues.requestTime;
        return `${day} ${month} ${year} (${time})`;
      }
      return '';
    },
    formattedDeliveredDateTime() {
      if (this.formValues.defaultDeliveryDate && this.formValues.defaultDeliveryDue) {
        const datePart = new Date(this.formValues.defaultDeliveryDate);
        const day = datePart.getDate();
        const month = datePart.toLocaleString('en', { month: 'short' });
        const year = datePart.getFullYear();
        const time = this.formValues.defaultDeliveryDue;
        return this.appendUrgentIndicator(`${day} ${month} ${year} (${time})`);
      }
      return '';
    },
    formattedPrimaryDateTime() {
      if (this.formValues.writingDueDate && this.formValues.writingDue) {
        const datePart = new Date(this.formValues.writingDueDate);
        const day = datePart.getDate();
        const month = datePart.toLocaleString('en', { month: 'short' });
        const year = datePart.getFullYear();
        const time = this.formValues.writingDue;
        return `${day} ${month} ${year} (${time})`;
      }
      return '';
    },
    formattedSecondaryDateTime() {
      if (this.formValues.editingDueDate && this.formValues.editingDue) {
        const datePart = new Date(this.formValues.editingDueDate);
        const day = datePart.getDate();
        const month = datePart.toLocaleString('en', { month: 'short' });
        const year = datePart.getFullYear();
        const time = this.formValues.editingDue;
        return `${day} ${month} ${year} (${time})`;
      }
      return '';
    },
    formattedQcDateTime() {
      const qcDate = this.formValues.qcDueDate || this.formValues.reviewingDueDate;
      const qcTime = this.formValues.qcDue || this.formValues.reviewingDue;
      if (qcDate && qcTime) {
        const datePart = new Date(qcDate);
        const day = datePart.getDate();
        const month = datePart.toLocaleString('en', { month: 'short' });
        const year = datePart.getFullYear();
        const time = qcTime;
        return `${day} ${month} ${year} (${time})`;
      }
      return '';
    },
    formattedDeliveryEtDateTime() {
      if (this.formValues.deliveryDate && this.formValues.deliveryDue) {
        const datePart = new Date(this.formValues.deliveryDate);
        const day = datePart.getDate();
        const month = datePart.toLocaleString('en', { month: 'short' });
        const year = datePart.getFullYear();
        const time = this.formValues.deliveryDue;
        return this.appendUrgentIndicator(`${day} ${month} ${year} (${time})`);
      }
      return '';
    },
  },
  methods: {
    ...mapActions("spreadsheetUpload", [
      "setFormValue",
    ]),
    onRequestDateSelected(date) {
      this.setFormValue({ key: 'requestDate', value: date });
    },
    onRequestTimeSelected(timeAmPm) {
      this.setFormValue({ key: 'requestTime', value: timeAmPm });
    },
    onDeliveryDateSelected(date) {
      this.setFormValue({ key: 'deliveryDate', value: date });
    },
    onDeliveryTimeSelected(timeAmPm) {
      this.setFormValue({ key: 'deliveryDue', value: timeAmPm });
    },
    onDefaultDeliveryDateSelected(date) {
      this.setFormValue({ key: 'defaultDeliveryDate', value: date });
    },
    onDefaultDeliveryTimeSelected(timeAmPm) {
      this.setFormValue({ key: 'defaultDeliveryDue', value: timeAmPm });
    },
    onPrimaryDateSelected(date) {
      this.setFormValue({ key: 'writingDueDate', value: date });
    },
    onPrimaryTimeSelected(timeAmPm) {
      this.setFormValue({ key: 'writingDue', value: timeAmPm });
    },
    onSecondaryDateSelected(date) {
      this.setFormValue({ key: 'editingDueDate', value: date });
    },
    onSecondaryTimeSelected(timeAmPm) {
      this.setFormValue({ key: 'editingDue', value: timeAmPm });
    },
    onQCDateSelected(date) {
      this.setFormValue({ key: 'qcDueDate', value: date });
      this.setFormValue({ key: 'reviewingDueDate', value: date });
    },
    onQCTimeSelected(timeAmPm) {
      this.setFormValue({ key: 'qcDue', value: timeAmPm });
      this.setFormValue({ key: 'reviewingDue', value: timeAmPm });
    },
    appendUrgentIndicator(value) {
      if (!value) return value;
      return this.formValues.urgent ? `${value} [URGENT]` : value;
    },
    parseDeadlineDate(value) {
      if (!value) return null;
      if (this.$moment.isMoment(value)) return value.clone();
      const candidate = this.$moment(value, [
        this.$moment.ISO_8601,
        "YYYY-MM-DD",
        "MM-DD-YYYY",
        "MM/DD/YYYY",
        "DD-MM-YYYY",
        "DD/MM/YYYY",
      ], true);
      if (candidate.isValid()) return candidate;
      const fallback = this.$moment(value);
      return fallback.isValid() ? fallback : null;
    },
    resolveUrgencyReferenceDate() {
      const visibleRequestDate = this.parseDeadlineDate(this.formValues.requestDate);
      if (visibleRequestDate) return visibleRequestDate;
      return this.parseDeadlineDate(this.tableRows?.[0]?.request_date);
    },
    updateUrgentFlag(deliveryDate = this.formValues.defaultDeliveryDate || this.formValues.deliveryDate) {
      const deliveryMoment = this.parseDeadlineDate(deliveryDate);
      const referenceDate = this.resolveUrgencyReferenceDate();
      const isUrgent =
        !!deliveryMoment &&
        !!referenceDate &&
        deliveryMoment.isSameOrBefore(referenceDate.clone().add(2, "days"));
      if (this.formValues.urgent !== isUrgent) {
        this.setFormValue({ key: "urgent", value: isUrgent });
      }
      return isUrgent;
    },
    convertDeliveryDue() {
      if (!this.formValues.defaultDeliveryDate || !this.formValues.defaultDeliveryDue || !this.timeZone) {
        this.conversionErrors = true;
      } else {
        this.conversionErrors = false;
        console.log("convertDeliveryDue", this.formValues.deliveryDate, this.formValues.deliveryDue, this.timeZone);
        const clientDeliveryData = new Date(
          `${this.$moment(this.formValues.defaultDeliveryDate).format("YYYY-MM-DD")
          }T${this.$moment(this.formValues.defaultDeliveryDue, "hh:mm A").format(
            "HH:mm"
          )}:00.000${this.timeZone.split(" ").at(-1)}`
        );
        console.log("clientDeliveryData", clientDeliveryData);
        const convertedDeliveryData = clientDeliveryData.toLocaleString(
          "en-US",
          {
            timeZone: "America/New_York",
          }
        );
        console.log("convertedDeliveryData", convertedDeliveryData);
        const convertedDeliveryDate = this.$moment(convertedDeliveryData).format("YYYY-MM-DD");
        const isUrgentDeadline = this.updateUrgentFlag(convertedDeliveryDate);
        this.setFormValue({
          key: "deliveryDate",
          value: convertedDeliveryDate,
        });
        this.setFormValue({
          key: "deliveryDue",
          value: clientDeliveryData.toLocaleString("en-US", {
            timeZone: "America/New_York",
            hour: "2-digit",
            minute: "2-digit",
          }),
        });
        if (isUrgentDeadline) {
          if (this.checkStaffInvolved("reviewer")) {
            let convertedReviewerData = this.$moment(
              convertedDeliveryData
            ).subtract(3, "hours");
            this.setFormValue({
              key: "reviewingDueDate",
              value: this.$moment(convertedReviewerData._d).format(
                "YYYY-MM-DD"
              ),
            });
            this.setFormValue({
              key: "reviewingDue",
              value: this.$moment(convertedReviewerData._d).format("hh:mm A"),
            });
          }
          if (this.checkStaffInvolved("editor")) {
            let convertedEditorData = this.$moment(
              convertedDeliveryData
            ).subtract(6, "hours");
            this.setFormValue({
              key: "editingDueDate",
              value: this.$moment(convertedEditorData._d).format("YYYY-MM-DD"),
            });
            this.setFormValue({
              key: "editingDue",
              value: this.$moment(convertedEditorData._d).format("hh:mm A"),
            });
          }
          let convertedWriterData = this.$moment(
            convertedDeliveryData
          ).subtract(9, "hours");
          this.setFormValue({
            key: "writingDueDate",
            value: this.$moment(convertedWriterData._d).format("YYYY-MM-DD"),
          });
          this.setFormValue({
            key: "writingDue",
            value: this.$moment(convertedWriterData._d).format("hh:mm A"),
          });
        } else {
          if (this.checkStaffInvolved("reviewer")) {
            let convertedReviewerData = this.$moment(
              convertedDeliveryData
            ).subtract(6, "hours");
            this.setFormValue({
              key: "reviewingDueDate",
              value: this.$moment(convertedReviewerData._d).format(
                "YYYY-MM-DD"
              ),
            });
            this.setFormValue({
              key: "reviewingDue",
              value: this.$moment(convertedReviewerData._d).format("hh:mm A"),
            });
          }
          if (this.checkStaffInvolved("editor")) {
            let convertedEditorData = this.$moment(
              convertedDeliveryData
            ).subtract(18, "hours");
            this.setFormValue({
              key: "editingDueDate",
              value: this.$moment(convertedEditorData._d).format("YYYY-MM-DD"),
            });
            this.setFormValue({
              key: "editingDue",
              value: this.$moment(convertedEditorData._d).format("hh:mm A"),
            });
          }
          let convertedWriterData = this.$moment(
            convertedDeliveryData
          ).subtract(24, "hours");
          this.setFormValue({
            key: "writingDueDate",
            value: this.$moment(convertedWriterData._d).format("YYYY-MM-DD"),
          });
          this.setFormValue({
            key: "writingDue",
            value: this.$moment(convertedWriterData._d).format("hh:mm A"),
          });
        }
      }
    },
    checkStaffInvolved(specialist) {
      const staffAssignments = Object.entries(this.staffAssignments);
      const geos = staffAssignments.map((el) => {
        return Object.entries(el[1]);
      });
      console.log("geos", staffAssignments, geos);
      const staff = [];
      geos.forEach((geo) => {
        geo.forEach((el) => {
          if (el[0] !== "wordCount") staff.push(el[1]);
        });
      });
      return staff.some((el) => el?.[specialist]);
    },
  },
  mounted() {
    this.formValues.requestDate = this.$moment().format("YYYY-MM-DD");
    this.formValues.requestTime = this.$moment().format("hh:mm A");
    this.updateUrgentFlag(this.formValues.defaultDeliveryDate || this.formValues.deliveryDate);
  }
};
</script>

<style scoped lang="scss">
.shared-deadlines {
  h4 {
    font-family: Roboto;
    font-weight: 400;
    font-size: 18px;
    line-height: 20px;
    letter-spacing: 0%;
    margin: 15px 0;
  }

  .date-time-picker-menu-content {
    width: fit-content;
  }
}

.info-form__dropdown-button {
  width: 56px;
  height: 56px;
  border-radius: 8px;
  border: 1px solid #E0E4EB;
  background-color: #f5f5f5;
}

.wtMenuList__item {
  cursor: pointer;
}

.deadline-dialog {
  background-color: #fff;
  max-width: 500px;
  height: 200px;
  padding: 15px;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  &__title {
    font-family: "Roboto", sans-serif;
    text-align: center;
    font-weight: 500;
    font-size: 20px;
    line-height: 18px;
    letter-spacing: 0%;
    margin-top: 30px;
  }
  &__text {
    font-family: "Roboto", sans-serif;
    text-align: center;
    font-weight: 300;
    font-size: 14px;
    line-height: 18px;
    letter-spacing: 0%;
  }
  &__buttons {
    display: flex;
    justify-content: flex-end;
    &--confirm {
      color: #fff;
    }
  }
}
</style>
